import type { BackendResponse, AdditionalInfo } from '@/types';

/**
 * Response parsing and transformation utilities
 */

/**
 * Parse text response that may contain JSON within markdown code blocks
 */
export function parseTextResponse(responseText: string): BackendResponse {
  try {
    let jsonString = responseText;

    // Find start and end positions explicitly
    const startPattern = /```(?:json)?/;
    const startMatch = jsonString.match(startPattern);

    if (startMatch) {
      const startIndex = startMatch.index! + startMatch[0].length;
      const endIndex = jsonString.indexOf('```', startIndex);

      if (endIndex !== -1) {
        jsonString = jsonString.slice(startIndex, endIndex).trim();
      }
    }

    return JSON.parse(jsonString);
  } catch {
    // If it's not JSON, treat it as a plain text answer
    return {
      answer: responseText,
      answer_nl: responseText, // Use same text for both languages as fallback
    };
  }
}

/**
 * Transform response with format/response structure to standard format
 */
export function transformFormattedResponse(
  response: BackendResponse
): BackendResponse {
  if (!response.format || !response.response) {
    return response;
  }

  if (response.format === 'text') {
    return parseTextResponse(response.response);
  }

  // For other formats, treat as plain text
  return {
    answer: response.response,
    answer_nl: response.response,
  };
}

/**
 * Build additional info object from response data
 */
export function buildAdditionalInfo(
  response: BackendResponse
): AdditionalInfo | undefined {
  const additionalInfo: AdditionalInfo = {};

  // Add bike information if available
  if (response.bike_info) {
    additionalInfo.bicycle_details = {
      model: response.bike_info.model,
      sku: response.bike_info.sku,
      snippet: response.bike_info.snippet,
    };
  }

  // Add sources if available (new format)
  if (
    response.sources &&
    Array.isArray(response.sources) &&
    response.sources.length > 0
  ) {
    additionalInfo.related_articles = response.sources.map((source) => ({
      title: source.source || 'Unknown Source',
      summary: source.snippet || '',
      link: source.document_id || '',
      score: source.score,
      source: source.source,
    }));
  }
  // Fallback to old articles format if sources not available
  else if (
    response.articles &&
    Array.isArray(response.articles) &&
    response.articles.length > 0
  ) {
    additionalInfo.related_articles = response.articles.map((article) => ({
      title: article.title,
      summary: article.summary,
      link: article.link,
      category: article.category,
      score: article.score,
    }));
  }

  return Object.keys(additionalInfo).length > 0 ? additionalInfo : undefined;
}
